/**
 * Encoding for an estimate's `selectedInclusionIds` / `selectedExclusionIds`.
 *
 * These columns hold a JSON array that historically contained ONLY ExclusionItem
 * row IDs (strings). Every consumer resolved them by filtering the tenant's
 * ExclusionItem rows: `items.filter(i => ids.includes(i.id))`.
 *
 * That silently dropped estimator-added ("custom") items. The panel minted a
 * client-only id — `custom-${Date.now()}` — which matches no row, and the item's
 * TEXT was never persisted anywhere, so it vanished from the proposal, the PDF
 * and even a page reload.
 *
 * Custom items can't just be written to ExclusionItem: that table is tenant-global
 * (a one-off exclusion would pollute the preset list for every future estimate)
 * and `POST /api/exclusions` is admin/manager-only, so estimators would get a 403.
 *
 * So the array now holds EITHER a plain string (a preset row id, unchanged) OR an
 * object `{ t: "text" }` carrying a custom item's text inline. This needs no schema
 * change — it ships with the normal code deploy rather than a manual `prisma db
 * push` — and it is backward compatible in both directions: existing rows are all
 * strings and parse exactly as before, and an older client reading a newer row just
 * skips the objects (degrading to today's behaviour rather than breaking).
 *
 * Keep this file in sync with `paver-backend/src/utils/inclusion-selection.ts`.
 */

/** One entry: a preset ExclusionItem id, or a custom item's text. */
export type SelectionEntry = string | { t: string }

export interface ParsedSelection {
  /** Preset ExclusionItem row ids. */
  ids: string[]
  /** Text of estimator-added custom items, in saved order. */
  customTexts: string[]
}

/**
 * Parse a stored selection column. Never throws — malformed JSON yields an empty
 * selection, matching the ignore-on-parse-failure behaviour the call sites had before.
 */
export function parseSelection(raw: string | null | undefined): ParsedSelection {
  let parsed: unknown
  try {
    parsed = JSON.parse(raw || '[]')
  } catch {
    return { ids: [], customTexts: [] }
  }
  if (!Array.isArray(parsed)) return { ids: [], customTexts: [] }

  const ids: string[] = []
  const customTexts: string[] = []
  for (const entry of parsed) {
    if (typeof entry === 'string') {
      ids.push(entry)
    } else if (entry && typeof entry === 'object' && typeof (entry as { t?: unknown }).t === 'string') {
      const text = (entry as { t: string }).t.trim()
      if (text) customTexts.push(text)
    }
  }
  return { ids, customTexts }
}

/** Serialize a selection back to the stored JSON shape. */
export function serializeSelection(ids: string[], customTexts: string[]): string {
  const entries: SelectionEntry[] = [
    ...ids,
    ...customTexts.map((t) => t.trim()).filter(Boolean).map((t) => ({ t })),
  ]
  return JSON.stringify(entries)
}

/**
 * Resolve a stored selection to the list of texts to print.
 *
 * `items` must already be narrowed to the right type ('inclusion' vs 'exclusion').
 *
 * Preset items keep their previous ordering — that of the `items` array, i.e. the
 * DB's sortOrder — rather than the order ids appear in the selection. Custom items
 * are appended after them. Resolving in saved-array order instead would reshuffle
 * inclusions on proposals that already exist, so ordering is deliberately left alone.
 */
export function resolveSelectionTexts(
  raw: string | null | undefined,
  items: Array<{ id: string; text: string }>,
): string[] {
  const { ids, customTexts } = parseSelection(raw)
  const preset = ids.length > 0 ? items.filter((i) => ids.includes(i.id)).map((i) => i.text) : []
  return [...preset, ...customTexts]
}
